import { Component, OnDestroy, OnInit } from '@angular/core';
import { EditComponent } from '@workspaces/utils/edit-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkspaceService } from '@workspaces/services/workspace.service';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { AppState } from 'src/app/state/app.state';
import { Store } from '@ngrx/store';
import { MatList, MatListItem } from '@angular/material/list';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatDivider } from '@angular/material/divider';
import { BehaviorSubject, combineLatest, interval, map, Observable, startWith, Subscription } from 'rxjs';
import { WorkspaceCollection, WorkspaceDocumentFile, WorkspaceDocumentsResponse } from '@workspaces/models/documents';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkspaceSourceDocFetch, workspaceSourceDocFeature } from '@workspaces/store/workspace-source-doc.feature';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { WorkspaceFileDelete, workspaceFileFeature } from '@workspaces/store/workspace-file-delete.feature';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import {
  WorkspaceCollectionDelete,
  workspaceCollectionFeature
} from '@workspaces/store/workspace-collection-delete.feature';
import { CopyCollectionModalComponent } from '../copy-collection-modal/copy-collection-modal.component';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { UploadSourceDocsComponent } from '../upload-source-docs/upload-source-docs.component';
import { CommonErrorLoadingComponent } from '../common-error-loading/common-error-loading.component';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';
import { DocumentStatusPipe } from '@workspaces/utils/pipes/document-status.pipe';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { DocumentStatusColorPipe } from '../../utils/pipes/document-status-color.pipe';
import { WorkspaceAuthService } from '@workspaces/utils/workspace-auth.service';

@Component({
  selector: 'app-choose-source-doc',
  templateUrl: './choose-source-doc.component.html',
  styleUrls: ['./choose-source-doc.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIcon,
    MatIconButton,
    MatList,
    MatListItem,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatDivider,
    DatePipe,
    MatExpansionPanelTitle,
    MatTooltipModule,
    MatProgressSpinner,
    MatMenuModule,
    MatButton,
    RequiredRoleDirective,
    CommonErrorLoadingComponent,
    DocumentStatusPipe,
    MatTabGroup,
    MatTab,
    DocumentStatusColorPipe
  ]
})
export class ChooseSourceDocComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  private readonly activeTabIndex$ = new BehaviorSubject(0);
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceService: WorkspaceService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly workspaceAuthService: WorkspaceAuthService
  ) {
    super(route, snackbar, router);
  }
  public selectedStatusDoc$!: Observable<WorkspaceDocumentsResponse>;
  public currentSourceDoc$!: Observable<WorkspaceDocumentsResponse>;
  public currentSourceDocIsError$!: Observable<boolean>;
  public currentSourceDocIsLoading$!: Observable<boolean>;
  public isDisabledEdit$!: Observable<boolean>;
  public currentFilesBeingDeleted: Record<string, boolean> = {};
  public currentCollectionsBeingDeleted: Record<string, boolean> = {};
  public statusNumberOfDoc: { wait: number; processing: number; processed: number; failed: number } = {
    failed: 0,
    processed: 0,
    processing: 0,
    wait: 0
  };

  override ngOnInit() {
    super.ngOnInit();

    this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));
    const ONE_MINUTE_MS = 60000;

    this.sub.add(
      interval(ONE_MINUTE_MS).subscribe(() => {
        this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
      })
    );
    this.isDisabledEdit$ = this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).pipe(
      map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted || workspace?.state === 'FINISHED'),
      startWith(false)
    );

    this.selectedStatusDoc$ = combineLatest([
      this.store.select(workspaceSourceDocFeature.workspaceSourceDocData(this.workspaceId)),
      this.activeTabIndex$.asObservable()
    ]).pipe(
      map(([workspaceDoc, activeTab]) => {
        const workspaceDocClone = structuredClone(workspaceDoc);
        if (!workspaceDocClone) {
          return { collections: [], files: [], workspace_id: '' };
        }

        //Separate observable not required as this should update with doc filter
        this.statusNumberOfDoc = {
          failed: 0,
          processed: 0,
          processing: 0,
          wait: 0
        };
        workspaceDocClone.files.forEach(file => {
          if (file.status.toUpperCase() === 'WAITING_FOR_UPLOAD') {
            this.statusNumberOfDoc.wait += 1;
          } else if (file.status.toUpperCase() === 'FAILED') {
            this.statusNumberOfDoc.failed += 1;
          } else if (file.status.toUpperCase() === 'PROCESSED') {
            this.statusNumberOfDoc.processed += 1;
          } else if (file.status.toUpperCase() === 'PROCESSING') {
            this.statusNumberOfDoc.processing += 1;
          }
        });

        if (activeTab === 1) {
          workspaceDocClone.files = workspaceDocClone.files.filter(
            file => file.status.toUpperCase() === 'WAITING_FOR_UPLOAD'
          );
        } else if (activeTab === 2) {
          workspaceDocClone.files = workspaceDocClone.files.filter(file => file.status.toUpperCase() === 'PROCESSING');
        } else if (activeTab === 3) {
          workspaceDocClone.files = workspaceDocClone.files.filter(file => file.status.toUpperCase() === 'PROCESSED');
        } else if (activeTab === 4) {
          workspaceDocClone.files = workspaceDocClone.files.filter(file => file.status.toUpperCase() === 'FAILED');
        }

        return workspaceDocClone;
      })
    );

    this.currentSourceDocIsError$ = combineLatest([
      this.store.select(workspaceSourceDocFeature.workspaceSourceDocData(this.workspaceId)),
      this.store.select(workspaceSourceDocFeature.workspaceSourceIsError(this.workspaceId))
    ]).pipe(
      map(([hasData, isError]) => {
        if (!hasData && isError) {
          return true;
        } else return false;
      })
    );
    this.currentSourceDocIsLoading$ = this.store.select(
      workspaceSourceDocFeature.workspaceSourceDocIsLoading(this.workspaceId)
    );
    this.sub.add(
      this.store.select(workspaceFileFeature.workspaceFilesAreBeingDeleted).subscribe(deleteFiles => {
        this.currentFilesBeingDeleted = deleteFiles;
      })
    );
    this.sub.add(
      this.store.select(workspaceCollectionFeature.workspaceCollectionsAreBeingDeleted).subscribe(deleteCollections => {
        this.currentCollectionsBeingDeleted = deleteCollections;
      })
    );
  }

  refetchDocs() {
    this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
  }

  onTabChange(event: { index: number }) {
    this.activeTabIndex$.next(event.index);
  }

  deleteStandaloneFile(file: WorkspaceDocumentFile) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: {
        message: `Do you want to delete ${file.file_name ?? 'Unknown'} file from the workspace source documents?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(WorkspaceFileDelete({ fileId: file.file_id, workspaceId: this.workspaceId }));
      }
    });
  }
  deleteCollection(collection: WorkspaceCollection) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to delete ${collection.name ?? 'Unknown'} collection with ${collection?.files?.length ?? 0} with files from the workspace source documents?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(WorkspaceCollectionDelete({ collectionId: collection.id, workspaceId: this.workspaceId }));
      }
    });
  }

  openCopyDialog(): void {
    const dialogRef = this.dialog.open(CopyCollectionModalComponent, {
      maxWidth: '800px',
      data: this.workspaceId
    });

    this.sub.add(
      dialogRef.afterClosed().subscribe(() => {
        this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
      })
    );
  }

  openUploadDialog(): void {
    const dialogRef = this.dialog.open(UploadSourceDocsComponent, {
      maxWidth: '800px',
      width: '500px',
      data: this.workspaceId
    });

    this.sub.add(
      dialogRef.afterClosed().subscribe(() => {
        this.store.dispatch(WorkspaceSourceDocFetch({ workspaceId: this.workspaceId }));
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
